import { useEffect, useState, useRef } from "react";
import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { z } from "zod";

// Define schools for the dropdown
const SCHOOLS = [
  "University of Alabama",
  "University of Arizona",
  "Arizona State University",
  "University of Arkansas",
  "Auburn University",
  "Baylor University",
  "Boston College",
  "University of California, Berkeley",
  "University of California, Los Angeles (UCLA)",
  "University of Cincinnati",
  "Clemson University",
  "University of Colorado",
  "Duke University",
  "University of Florida",
  "Florida State University",
  "University of Georgia",
  "Georgia Tech",
  "University of Illinois",
  "Indiana University",
  "University of Iowa",
  "Iowa State University",
  "University of Kansas",
  "Kansas State University",
  "University of Kentucky",
  "Louisiana State University (LSU)",
  "University of Louisville",
  "University of Maryland",
  "University of Miami",
  "University of Michigan",
  "Michigan State University",
  "University of Minnesota",
  "University of Mississippi (Ole Miss)",
  "Mississippi State University",
  "University of Missouri",
  "University of Nebraska",
  "University of North Carolina",
  "North Carolina State University",
  "Northwestern University",
  "University of Notre Dame",
  "Ohio State University",
  "University of Oklahoma",
  "Oklahoma State University",
  "University of Oregon",
  "Oregon State University",
  "Pennsylvania State University",
  "University of Pittsburgh",
  "Purdue University",
  "Rutgers University",
  "University of South Carolina",
  "University of Southern California (USC)",
  "Stanford University",
  "Syracuse University",
  "University of Tennessee",
  "University of Texas",
  "Texas A&M University",
  "Texas Christian University (TCU)",
  "Texas Tech University",
  "Vanderbilt University",
  "University of Virginia",
  "Virginia Tech",
  "Wake Forest University",
  "University of Washington",
  "Washington State University",
  "West Virginia University",
  "University of Wisconsin",
  // Add more schools as needed
];

const socialSignupSchema = z.object({
  username: z.string().min(3, "Username must be at least 3 characters").max(30, "Username cannot exceed 30 characters"),
  school: z.string().optional(),
  password: z.string().optional(),
  confirmPassword: z.string().optional(),
}).refine((data) => {
  // If password is provided, confirmPassword should match
  if (data.password && data.password !== data.confirmPassword) {
    return false;
  }
  return true;
}, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

type SocialSignupFormValues = z.infer<typeof socialSignupSchema>;

export default function SocialSignup() {
  const [_, navigate] = useLocation();
  const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const tempToken = searchParams.get('token');
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Use ref to store form data without causing re-renders
  const formRef = useRef<SocialSignupFormValues>({
    username: "",
    school: "",
    password: "",
    confirmPassword: "",
  });

  // For form validation
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  // Check if we have a token
  useEffect(() => {
    if (!tempToken) {
      // No token, redirect to login
      navigate("/login");
      toast({
        title: "Authentication Error",
        description: "Invalid or missing authentication token.",
        variant: "destructive",
      });
    }
  }, [tempToken, navigate, toast]);

  // Mutation for completing social signup
  const completeSocialSignup = useMutation({
    mutationFn: async (data: SocialSignupFormValues) => {
      return apiRequest("POST", "/api/auth/complete-social", {
        tempToken,
        username: data.username,
        school: data.school || undefined,
        password: data.password || undefined,
      });
    },
    onSuccess: (data: any) => {
      // Store session
      localStorage.setItem("sessionId", data.sessionId || "");
      localStorage.setItem("user", JSON.stringify(data.user || {}));
      
      toast({
        title: "Registration Successful",
        description: "Your account has been created successfully!",
      });
      
      // Redirect to dashboard
      navigate("/dashboard");
    },
    onError: (error: any) => {
      setError(error.message || "Failed to complete registration");
      toast({
        title: "Registration Failed",
        description: error.message || "Failed to complete registration",
        variant: "destructive",
      });
    }
  });

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    const data = formRef.current;
    
    // Validate username
    if (!data.username) {
      errors.username = "Username is required";
    } else if (data.username.length < 3) {
      errors.username = "Username must be at least 3 characters";
    } else if (data.username.length > 30) {
      errors.username = "Username cannot exceed 30 characters";
    }
    
    // Validate password match if both are provided
    if (data.password && data.password !== data.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      completeSocialSignup.mutate(formRef.current);
    }
  };
  
  const handleInputChange = (field: keyof SocialSignupFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
    formRef.current[field] = e.target.value;
  };
  
  const handleSelectChange = (value: string) => {
    formRef.current.school = value;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-primary">Bloom</h1>
          <p className="text-neutral-500 mt-2">Complete your profile</p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Complete your profile</CardTitle>
            <CardDescription>
              We just need a few more details to complete your account setup.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="username">Username</Label>
                <Input 
                  id="username"
                  placeholder="Username" 
                  onChange={handleInputChange("username")}
                  defaultValue={formRef.current.username}
                  required
                />
                {formErrors.username && (
                  <p className="text-sm text-red-500 mt-1">{formErrors.username}</p>
                )}
              </div>

              <div>
                <Label htmlFor="school">School (Optional)</Label>
                <Select onValueChange={handleSelectChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your school" />
                  </SelectTrigger>
                  <SelectContent>
                    {SCHOOLS.map((school) => (
                      <SelectItem key={school} value={school}>
                        {school}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t border-gray-200 pt-4 mt-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Add a password if you want to log in directly in the future (optional)
                </p>

                <div className="mb-3">
                  <Label htmlFor="password">Password (Optional)</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Create a password"
                    onChange={handleInputChange("password")}
                    defaultValue={formRef.current.password}
                  />
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm password"
                    onChange={handleInputChange("confirmPassword")}
                    defaultValue={formRef.current.confirmPassword}
                  />
                  {formErrors.confirmPassword && (
                    <p className="text-sm text-red-500 mt-1">{formErrors.confirmPassword}</p>
                  )}
                </div>
              </div>

              <div className="pt-4">
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={completeSocialSignup.isPending}
                >
                  {completeSocialSignup.isPending ? "Creating Account..." : "Complete Registration"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}